let _ = t => t,
  _t;
import { createCopilotChannel } from '../channel/createCopilotChannel';
import { COPILOT_APP_CONTAINER_CLASS, COPILOT_GRID_LAYOUT_CLASS, COPILOT_SIDEBAR_CONTAINER_CLASS, PANEL_WITH_SCROLL_CLASS, SIDEBAR_CLASS } from '../constants/LayoutClasses';
import { createRoot } from 'react-dom/client';
import { render } from 'react-dom';
import { createGlobalStyle } from 'styled-components';
import { COPILOT_INITIALIZE_OPEN } from 'chatspot-core/constants/SessionStorage';

/**
 * Add Copilot styles to the Copilot layout components
 *
 * 1. PANEL_WITH_SCROLL_CLASS - For the panel-with-scroll layout, adds a  grid
 * layout where the first section (the app) gets all the space in the parent
 * container and the 2nd section (the sidebar spacer) gets no space. This
 * places the sidebar spacer offscreen so we can scroll over to it without
 * scrolling the app.
 *
 * 2. SIDEBAR_CLASS - For the sidebar layout, adds a grid layout where the first
 * section (the app) gets all the space in the parent container but the sidebar
 * has a higher priority for space when it needs it (auto). This lets the
 * sidebar "push" the app over when it render.
 *   2a. COPILOT_SIDEBAR_CONTAINER_CLASS - The sidebar container is given a
 *       width and overflow hidden when it starts open to reserve space for the
 *       sidebar to open in when it loads.
 *
 * 3. COPILOT_APP_CONTAINER_CLASS - The app container is given a min-width and
 * min-height so it works in flex containers, we also set the overflow-y to auto
 * so scrollbars will appear for the app in the app window and not the main
 * window.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CopilotStyles = createGlobalStyle(_t || (_t = _`
  .${0} {
    &.${0} {
      display: grid;
      max-width: 100%;
      grid-template-columns: 1fr 0px;
    }
    &.${0} {
      display: grid;
      max-width: 100%;
      grid-template-columns: 1fr auto;
      > .${0} {
        background: #FFF;
        border-radius: 16px 0px 0px 16px;
        ${0}
      }
    }
    > .${0} {
      min-width: 0;
      min-height: 0;
      overflow-y: auto;
      max-height: inherit;
    }
  }
`), COPILOT_GRID_LAYOUT_CLASS, PANEL_WITH_SCROLL_CLASS, SIDEBAR_CLASS, COPILOT_SIDEBAR_CONTAINER_CLASS, ({
  shouldStartOpen
}) => shouldStartOpen && `
          width: 400px;
          overflow: hidden;
        `, COPILOT_APP_CONTAINER_CLASS);
const getShouldStartOpen = () => {
  try {
    return sessionStorage.getItem(COPILOT_INITIALIZE_OPEN) === 'true';
  } catch (e) {
    return false;
  }
};
const renderApp = (app, renderTarget, {
  useCreateRoot
}) => {
  const shouldStartOpen = getShouldStartOpen();
  if (useCreateRoot) {
    createRoot(renderTarget).render( /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(CopilotStyles, {
        "data-test-id": "hi",
        shouldStartOpen: shouldStartOpen
      }), app]
    }));
  } else {
    render( /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(CopilotStyles, {
        "data-test-id": "hi",
        shouldStartOpen: shouldStartOpen
      }), app]
    }), renderTarget);
  }
};
export const renderWithNavCopilot = (app, {
  fallbackRenderTarget = document.getElementsByClassName('page')[0],
  layoutType,
  useCreateRoot
}) => {
  const gridContainer = document.querySelector(`.${COPILOT_GRID_LAYOUT_CLASS}`);
  const appContainer = document.querySelector(`.${COPILOT_APP_CONTAINER_CLASS}`);
  try {
    const channel = createCopilotChannel();
    channel.setState({
      layoutType
    });
  } catch (e) {
    // Failed to set layout type
    console.error(e);
  }
  if (layoutType === 'panel') {
    const maybeSidebarElement = document.querySelector(`.${COPILOT_SIDEBAR_CONTAINER_CLASS}`);

    // Move the sidebar element to the body so it doesn't get deleted by the app
    // when it renders. It doesn't matter where the element is as long as it's
    // on the page for the panel layout so we just put it on the body
    if (maybeSidebarElement) {
      document.body.appendChild(maybeSidebarElement);
    }
    renderApp(app, fallbackRenderTarget, {
      useCreateRoot
    });
    return;
  }

  // Make sure both the required layout containers are here before trying to
  // render a copilot layout
  if (gridContainer && appContainer) {
    if (layoutType === 'panel-with-scroll') {
      gridContainer.classList.add(PANEL_WITH_SCROLL_CLASS);
      renderApp(app, appContainer, {
        useCreateRoot
      });
      return;
    } else if (layoutType === 'sidebar') {
      gridContainer.classList.add(SIDEBAR_CLASS);
      renderApp(app, appContainer, {
        useCreateRoot
      });
      return;
    }
  }
  return renderApp(app, fallbackRenderTarget, {
    useCreateRoot
  });
};